import React, { useState, useEffect } from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "./ui/select";
import { fmtDwell } from "../lib/wagonColors";
import { DEST_CODES, EMPTY_DEST, destLabel } from "../lib/destinations";
import { statusLabel, formatActor, localeFor, wagonNoteView } from "../i18n";
import { hasBogieImbalance, bogieSkewRatio, BOGIE_IMBALANCE_THRESHOLD } from "../lib/bogie";
import { toast } from "sonner";
import {
  X,
  Save,
  ArrowRightLeft,
  Clock,
  Gauge,
  MapPin,
  Hash,
  ShieldAlert,
  Scale,
} from "lucide-react";

export const WagonDetail = ({ wagon, t, lang, onSave, onMove, trackOptions, onClose }) => {
  const [form, setForm] = useState(wagon);
  const [moveTrack, setMoveTrack] = useState("");
  useEffect(() => setForm(wagon), [wagon]);

  if (!wagon) return null;

  const dwellSeconds = wagon.dwell_seconds ?? 0;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const { note, consist } = wagonNoteView(form.note, wagon.consistTrainNo, lang);

  const save = () => {
    const brutto = parseFloat(form.weight_brutto);
    const netto = parseFloat(form.weight_netto);
    onSave(wagon.id, {
      number: form.number,
      cargo: form.cargo,
      weight_brutto: Number.isNaN(brutto) ? wagon.weight_brutto : brutto,
      weight_netto: Number.isNaN(netto) ? wagon.weight_netto : netto,
      destination: form.destination,
      owner: form.owner,
      note,
    });
    toast.success(t("saved"));
  };

  const doMove = () => {
    if (moveTrack === "") return;
    const res = onMove(wagon.id, parseInt(moveTrack, 10));
    if (res && res.ok) toast.success(t("moved"));
    else if (res && res.reason === "coupled") toast.error(t("coupledError"));
    else if (res && res.reason === "full") toast.error(t("trackFullError"));
    else toast.error(t("cantMove"));
    setMoveTrack("");
  };

  const allTracks = trackOptions || [];

  const confColor =
    wagon.ocr_confidence >= 0.9
      ? "#10b981"
      : wagon.ocr_confidence >= 0.7
      ? "#f59e0b"
      : "#f43f5e";

  const bogie1 = wagon.bogie1_t ?? null;
  const bogie2 = wagon.bogie2_t ?? null;
  const skewed = hasBogieImbalance(wagon);
  const skewPct = Math.round(bogieSkewRatio(bogie1, bogie2, wagon.weight_brutto) * 1000) / 10;

  return (
    <div className="flex flex-col h-full" data-testid="wagon-detail-panel">
      <div className="flex items-center justify-between p-4 border-b border-zinc-800">
        <div className="flex items-center gap-2">
          <Hash size={16} className="text-sky-400" />
          <span className="font-mono text-base font-bold text-white tracking-wider">
            {wagon.number}
          </span>
        </div>
        <button type="button" onClick={onClose} data-testid="wagon-close" className="text-zinc-500 hover:text-white">
          <X size={18} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto no-scrollbar">
        <div className="relative">
          <img
            src={wagon.photo_url}
            alt={t("photo")}
            className="w-full h-36 object-cover opacity-90"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#141417] to-transparent" />
          <div className="absolute bottom-2 left-3 right-3 flex items-center justify-between">
            <span className="text-[10px] font-mono bg-black/60 px-2 py-1 rounded-sm text-zinc-300">
              CAM · {wagon.number}
            </span>
            <span
              className="text-[10px] font-mono px-2 py-1 rounded-sm flex items-center gap-1"
              style={{ background: "rgba(0,0,0,0.6)", color: confColor }}
            >
              <ShieldAlert size={11} /> {t("confidence")} {(wagon.ocr_confidence * 100).toFixed(0)}%
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-px bg-zinc-800 border-b border-zinc-800">
          <QuickStat icon={Clock} label={t("dwellTime")} value={fmtDwell(dwellSeconds, t)} />
          <QuickStat icon={Gauge} label={t("bruttoW")} value={`${wagon.weight_brutto} ${t("tonneUnit")}`} />
          <QuickStat
            icon={MapPin}
            label={t("destination")}
            value={destLabel(wagon.destination, lang)}
          />
          <QuickStat
            icon={ArrowRightLeft}
            label={t("status")}
            value={statusLabel(wagon.status, t)}
          />
        </div>

        {bogie1 != null && bogie2 != null && (
          <div
            className={`mx-4 mt-3 rounded-sm border px-3 py-2.5 space-y-2 ${
              skewed ? "border-orange-500/60 bg-orange-500/10" : "border-zinc-800 bg-zinc-900/50"
            }`}
            data-testid="weighing-block"
          >
            <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-widest text-zinc-500">
              <Scale size={11} /> {t("weighing")}
            </div>
            <div className="grid grid-cols-3 gap-2">
              <WeighCell
                label={t("bogie1")}
                value={bogie1}
                alert={skewed}
                unit={t("tonneUnit")}
              />
              <WeighCell
                label={t("bogie2")}
                value={bogie2}
                alert={skewed}
                unit={t("tonneUnit")}
              />
              <WeighCell
                label={t("totalWeight")}
                value={wagon.weight_brutto}
                alert={false}
                unit={t("tonneUnit")}
              />
            </div>
            {skewed && (
              <p className="text-[11px] text-orange-300 leading-snug" data-testid="bogie-imbalance-warn">
                {t("bogieImbalanceWarn")} ({skewPct}% &gt; {BOGIE_IMBALANCE_THRESHOLD * 100}%)
              </p>
            )}
          </div>
        )}

        <div className="p-4 space-y-3">
          <Field label={t("number")}>
            <Input
              data-testid="edit-number"
              value={form.number}
              onChange={(e) => set("number", e.target.value)}
              className="bg-zinc-900 border-zinc-800 h-9 font-mono"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label={t("cargo")}>
              <Select value={form.cargo} onValueChange={(v) => set("cargo", v)}>
                <SelectTrigger data-testid="edit-cargo" className="bg-zinc-900 border-zinc-800 h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="coal">{t("coal")}</SelectItem>
                  <SelectItem value="empty">{t("empty")}</SelectItem>
                  <SelectItem value="foreign">{t("foreign")}</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label={t("owner")}>
              <Select value={form.owner} onValueChange={(v) => set("owner", v)}>
                <SelectTrigger data-testid="edit-owner" className="bg-zinc-900 border-zinc-800 h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="own">{t("own")}</SelectItem>
                  <SelectItem value="foreign">{t("foreign")}</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label={t("bruttoW")}>
              <Input
                data-testid="edit-brutto"
                type="number"
                value={form.weight_brutto}
                onChange={(e) => set("weight_brutto", e.target.value)}
                className="bg-zinc-900 border-zinc-800 h-9 font-mono"
              />
            </Field>
            <Field label={t("nettoW")}>
              <Input
                data-testid="edit-netto"
                type="number"
                value={form.weight_netto}
                onChange={(e) => set("weight_netto", e.target.value)}
                className="bg-zinc-900 border-zinc-800 h-9 font-mono"
              />
            </Field>
          </div>

          <Field label={t("destination")}>
            <Select
              value={form.destination}
              onValueChange={(v) => set("destination", v)}
            >
              <SelectTrigger data-testid="edit-destination" className="bg-zinc-900 border-zinc-800 h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DEST_CODES.concat(EMPTY_DEST).map((code) => (
                  <SelectItem key={code} value={code}>
                    {destLabel(code, lang)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          {consist ? (
            <p className="text-[12px] text-zinc-400" data-testid="consist-note">
              {consist}
            </p>
          ) : null}

          <Field label={t("note")}>
            <Textarea
              data-testid="edit-note"
              value={note}
              onChange={(e) => set("note", e.target.value)}
              className="bg-zinc-900 border-zinc-800 min-h-[56px] text-[12px]"
            />
          </Field>

          <Button
            data-testid="save-wagon"
            onClick={save}
            className="w-full h-9 gap-2 bg-sky-500 hover:bg-sky-400 text-white rounded-sm"
          >
            <Save size={15} /> {t("save")}
          </Button>

          <div className="pt-2 border-t border-zinc-800 space-y-2">
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">
              {t("moveTo")}
            </Label>
            <div className="flex gap-2">
              <Select value={moveTrack} onValueChange={setMoveTrack}>
                <SelectTrigger data-testid="move-track-select" className="bg-zinc-900 border-zinc-800 h-9 flex-1">
                  <SelectValue placeholder={t("moveTo")} />
                </SelectTrigger>
                <SelectContent>
                  {allTracks.map((tr) => (
                    <SelectItem key={tr.id} value={tr.id}>
                      {tr.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                data-testid="move-wagon-btn"
                onClick={doMove}
                variant="outline"
                className="h-9 gap-1.5 bg-transparent border-zinc-700 text-zinc-200 hover:bg-zinc-800 rounded-sm px-3"
              >
                <ArrowRightLeft size={15} />
                <span className="text-[11px]">{t("moveBtn")}</span>
              </Button>
            </div>
          </div>

          <p className="text-[10px] text-zinc-600 font-mono pt-1">
            {t("updated")}: {formatActor(wagon.updated_by, t)} · {new Date(wagon.updated_at).toLocaleTimeString(localeFor(lang))}
          </p>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div className="space-y-1">
    <Label className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</Label>
    {children}
  </div>
);

const QuickStat = ({ icon: Icon, label, value }) => (
  <div className="bg-[#141417] px-3 py-2.5">
    <div className="flex items-center gap-1.5 text-zinc-500 text-[9px] uppercase tracking-widest">
      <Icon size={11} /> {label}
    </div>
    <div className="text-[12px] text-zinc-100 font-medium mt-1 truncate">{value}</div>
  </div>
);

const WeighCell = ({ label, value, alert, unit }) => (
  <div>
    <div className="text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
    <div
      className={`text-[13px] font-mono font-semibold mt-0.5 ${
        alert ? "text-orange-400" : "text-zinc-100"
      }`}
    >
      {value} {unit}
    </div>
  </div>
);
